import { FormattedMessage } from "react-intl";
import colors from "assets/styles/mixins/colors.scss";
import { LANG } from "constants/constants";

export const COLOR_RULES = [
  {
    keywords: ["missions sans fin", "sans fin", "endless"],
    color: "purpleChart",
  },
  {
    keywords: [
      "non réglé",
      "non réglées",
      "ic non",
      "ed non",
      "cl non",
      "> 6 mois",
      "unpaid",
    ],
    color: "orangeChart",
  },
  {
    keywords: [
      "réglé",
      "réglées",
      "intercontrat",
      "congés longs",
      "mission en cours",
      "missions en cours",
      "< 6 mois",
      "paid",
      "ongoing",
    ],
    color: "greenChart",
  },
];

const chartTitleMap = {
  cl: "charts_cl",
  missions: "charts_mission",
  ic: "charts_ic",
  ed: "charts_ed",
  ratio: "charts_ratio",
  fae: "charts_fae",
};

export const getChartTitle = (type) => chartTitleMap[type] || "";

export const convertToNivoData = (chartData) => {
  if (!chartData || !Array.isArray(chartData) || chartData.length <= 1) {
    return [];
  }

  return chartData
    .slice(1) // Skip header row
    .filter(([label, value]) => label && value > 0)
    .map(([label, value, realValue]) => ({
      id: label,
      label,
      value: Number(value),
      realValue:
        realValue !== null && realValue !== undefined
          ? Number(realValue)
          : Number(value),
    }));
};

export const getSliceColorByLabel = (label) => {
  const lowerLabel = label.toLowerCase();

  for (const rule of COLOR_RULES) {
    if (rule.keywords.some((keyword) => lowerLabel.includes(keyword))) {
      return colors[rule.color];
    }
  }

  return colors.gray;
};

export const getColorMapping = (data) => {
  return data.reduce((colorMap, item) => {
    colorMap[item.id] = getSliceColorByLabel(item.label);
    return colorMap;
  }, {});
};

export const formatNumber = (number) =>
  number > 1000 ? `${Math.floor(number / 1000)}k` : number?.toString();

const formatPercentage = (number) => {
  if (number === undefined || number === null || isNaN(number)) {
    return undefined;
  }
  if (Number(number) === 100) {
    return "100";
  }
  return Number(number).toFixed(1);
};

// Arrondit un tableau de pourcentages à `decimals` décimales en garantissant
// que la somme arrondie atteint `target` (méthode "largest remainder", Hamilton).
// Évite l'artefact d'arrondi naïf où ex. [33.33, 33.33, 33.33] s'affiche 99.9 %.
const roundPercentagesToSum = (values, decimals = 1, target = 100) => {
  const safe = values.map((v) =>
    v === undefined || v === null || isNaN(v) ? 0 : Number(v)
  );
  const sum = safe.reduce((a, b) => a + b, 0);
  if (sum === 0) return safe.map(() => (0).toFixed(decimals));

  const factor = 10 ** decimals;
  const scaled = safe.map((v) => v * factor);
  const floored = scaled.map(Math.floor);
  const diff =
    Math.round(target * factor) - floored.reduce((a, b) => a + b, 0);

  if (diff > 0) {
    const order = scaled
      .map((s, i) => ({ i, rem: s - floored[i] }))
      .sort((a, b) => b.rem - a.rem);
    for (let k = 0; k < diff; k++) {
      floored[order[k % order.length].i] += 1;
    }
  }

  return floored.map((n) => (n / factor).toFixed(decimals));
};

export const getChartTitleNum = (type, title) => {
  switch (type) {
    case "cl":
    case "missions":
    case "ic":
    case "ed":
      return title?.total;
    case "ratio":
      const percentage = formatPercentage(title?.ic_percentage);
      return percentage ? percentage + " %" : undefined;
    case "fae":
      return formatNumber(title?.total);
    default:
      return "";
  }
};

const pluralRules = {};

const selectPlural = (lang, count) => {
  pluralRules[lang] = pluralRules[lang] || new Intl.PluralRules(lang);

  return pluralRules[lang].select(Number(count) || 0);
};

const translate = (lang, translations, count) => {
  const forms = lang === LANG.FR ? translations.fr : translations.en;

  if (typeof forms === "string") {
    return forms;
  }

  return selectPlural(lang, count) === "one" ? forms.one : forms.other;
};

const IC_KO_LABEL = {
  fr: { one: "IC non réglé", other: "IC non réglés" },
  en: "unpaid IC",
};

const SLICE_LABELS = {
  missions: {
    ok: { fr: { one: "FDM réglée", other: "FDM réglées" }, en: "paid EOM" },
    ko: {
      fr: { one: "FDM non réglée", other: "FDM non réglées" },
      en: "unpaid EOM",
    },
    current: {
      fr: { one: "mission sans fin", other: "missions sans fin" },
      en: { one: "endless mission", other: "endless missions" },
    },
    ongoing: { fr: "FDM en cours", en: "ongoing EOM" },
  },
  ratio: {
    missions: {
      fr: { one: "mission en cours", other: "missions en cours" },
      en: { one: "ongoing mission", other: "ongoing missions" },
    },
    ic: IC_KO_LABEL,
  },
  fae: {
    over: { fr: "FAE > 6 mois", en: "ITE > 6 months" },
    under: { fr: "FAE < 6 mois", en: "ITE < 6 months" },
  },
  ic: {
    ok: { fr: { one: "IC réglé", other: "IC réglés" }, en: "paid IC" },
    ko: IC_KO_LABEL,
    ongoing: { fr: "IC en cours", en: "ongoing IC" },
  },
  cl: {
    ok: { fr: { one: "CL réglé", other: "CL réglés" }, en: "LH paid" },
    ko: { fr: { one: "CL non réglé", other: "CL non réglés" }, en: "unpaid LH" },
    ongoing: { fr: "CL en cours", en: "ongoing LH" },
  },
  ed: {
    ok: { fr: { one: "ED réglé", other: "ED réglés" }, en: "paid DH" },
    ko: { fr: { one: "ED non réglé", other: "ED non réglés" }, en: "unpaid DH" },
    ongoing: { fr: "ED en cours", en: "ongoing DH" },
  },
};

const buildStandardTypeData = (type, data, lang, labels) => {
  return [
    [type, "", null],
    [translate(lang, labels.ok, data?.ok), data?.ok, null],
    [translate(lang, labels.ko, data?.ko), data?.ko, null],
    [translate(lang, labels.ongoing), 0, null],
    [
      translate(lang, labels.ongoing),
      !data?.ok && !data?.ko ? 100 : 0,
      0,
    ],
  ];
};

export const buildChartData = (type, data, lang) => {
  const typeMappings = {
    missions: [
      ["missions", "", null],
      [translate(lang, SLICE_LABELS.missions.ok, data?.ok), data?.ok, null],
      [translate(lang, SLICE_LABELS.missions.ko, data?.ko), data?.ko, null],
      [
        translate(lang, SLICE_LABELS.missions.current, data?.current),
        data?.current,
        null,
      ],
      [
        translate(lang, SLICE_LABELS.missions.ongoing),
        !data?.ok && !data?.ko && !data?.current ? 100 : 0,
        0,
      ],
    ],
    ratio: [
      ["ratio", "", null],
      [
        translate(lang, SLICE_LABELS.ratio.missions, data?.missions),
        data?.missions,
        null,
      ],
      [translate(lang, SLICE_LABELS.ratio.ic, data?.ic), data?.ic, null],
      [
        translate(lang, SLICE_LABELS.ratio.ic, data?.ic),
        !data?.missions && !data?.ic_percentage ? 100 : 0,
        0,
      ],
    ],
    fae: [
      ["fae", "", null],
      [translate(lang, SLICE_LABELS.fae.over), data?.over_6_months, null],
      [translate(lang, SLICE_LABELS.fae.under), data?.under_6_months, null],
      [
        // this extra row is necessary in order to display the correct color in the chart
        translate(lang, SLICE_LABELS.fae.under),
        0,
        null,
      ],
      [
        translate(lang, SLICE_LABELS.fae.under),
        !data?.over_6_months && !data?.under_6_months ? 100 : 0,
        0,
      ],
    ],
    ic: buildStandardTypeData("ic", data, lang, SLICE_LABELS.ic),
    cl: buildStandardTypeData("cl", data, lang, SLICE_LABELS.cl),
    ed: buildStandardTypeData("ed", data, lang, SLICE_LABELS.ed),
  };

  return typeMappings[type] || [];
};

const getBottomLegendMap = {
  charts_cl: {
    title: {
      subTitleOK: "charts_cl",
      subTitleKO: "charts_cl_ko",
    },
  },
  charts_mission: {
    title: {
      subTitleOK: "charts_mission_ok",
      subTitleKO: "charts_mission_ko",
      subTitleCurrent: "charts_mission_current",
    },
  },
  charts_ic: {
    title: {
      subTitleOK: "charts_ic",
      subTitleKO: "charts_ic_ko",
    },
  },
  charts_ed: {
    title: {
      subTitleOK: "charts_ed_ok",
      subTitleKO: "charts_ed_ko",
    },
  },
  charts_ratio: {
    title: {
      subTitleOK: "charts_mission",
      subTitleKO: "charts_ic_ko",
    },
  },
  charts_fae: {
    title: {
      subTitleOK: "charts_fae_ok",
      subTitleKO: "charts_fae_ko",
    },
  },
};

const LEGEND_FIELDS = {
  charts_fae: [
    ["under_6_months", "under_percentage"],
    ["over_6_months", "over_percentage"],
  ],
  charts_ratio: [
    ["missions", "missions_percentage"],
    ["ic", "ic_percentage"],
  ],
  charts_mission: [
    ["ok", "ok_percentage"],
    ["ko", "ko_percentage"],
    ["current", "current_percentage"],
  ],
};

const DEFAULT_LEGEND_FIELDS = [
  ["ok", "ok_percentage"],
  ["ko", "ko_percentage"],
];

const getLegendValues = (data) => {
  const fields = LEGEND_FIELDS[data?.titleLegend] ?? DEFAULT_LEGEND_FIELDS;
  const stats = data?.dataPercentage ?? {};

  return {
    counts: fields.map(([count]) => stats[count]),
    percentages: fields.map(([, percentage]) => stats[percentage]),
  };
};

export const renderLegends = (data) => {
  const colorOK = colors.greenChart;
  const colorKO = colors.orangeChart;

  const { counts, percentages } = getLegendValues(data);
  const rounded = roundPercentagesToSum(percentages, 1, 100).map((s) =>
    s === "100.0" ? "100" : s
  );

  const legendItems = [
    {
      id: "ok",
      color: colorOK,
      value: rounded[0],
      count: counts[0],
      subTitle: getBottomLegendMap[data.titleLegend]?.title?.subTitleOK,
    },
    {
      id: "ko",
      color: colorKO,
      value: rounded[1],
      count: counts[1],
      subTitle: getBottomLegendMap[data.titleLegend]?.title?.subTitleKO,
    },
  ];

  if (data?.titleLegend === "charts_mission") {
    legendItems.push({
      id: "current",
      color: colors.purpleChart,
      value: rounded[2],
      count: counts[2],
      subTitle: getBottomLegendMap[data.titleLegend]?.title?.subTitleCurrent,
    });
  }

  legendItems.sort((a, b) => {
    if (a.color === colors.purpleChart) {
      return -1;
    } else if (b.color === colors.purpleChart) {
      return 1;
    } else {
      return 0;
    }
  });

  return (
    <div className="legend">
      {legendItems?.map((item) => {
        return (
          item?.subTitle && (
            <div className="legend-item" key={`${item?.id}-${item?.value}`}>
              <div
                className="legend-item__color"
                style={{ backgroundColor: item?.color }}
              ></div>
              <div className="legend-item__text">
                {`${item?.value}% `}
                <FormattedMessage
                  id={item?.subTitle}
                  defaultMessage={"chart title"}
                  values={{ count: Number(item?.count) || 0 }}
                />
              </div>
            </div>
          )
        );
      })}
    </div>
  );
};
